import { useEffect, useRef, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import L from 'leaflet';
import { X, Navigation, Crosshair, ArrowRight, AlertTriangle, PlayCircle, Layers } from 'lucide-react';
import type { Chiamata } from '../shared/types';
import { apriGoogleMaps } from '../shared/maps';
import { apiPatch } from '../shared/apiClient';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../hooks/useToast';

interface MappaSquadraModalProps {
  isOpen: boolean;
  onClose: () => void;
  chiamate: Chiamata[];
  onSelectChiamata: (chiamataId: string) => void;
}

const ROMA_CENTER: [number, number] = [41.8902, 12.4922];
const DEFAULT_ZOOM = 12;

function getChiamataPinState(c: Chiamata) {
  if (c.inLavorazione) {
    return {
      color: '#10b981',
      stroke: '#ffffff',
      label: 'In Lavorazione',
      type: 'verde',
      isInCarico: true,
    };
  }
  const text = `${c.guastoSegnalato || ''} ${c.descrizione || ''}`.toLowerCase();
  if (
    text.includes('spento') ||
    text.includes('blocc') ||
    text.includes('pericol') ||
    text.includes('lampegg') ||
    text.includes('urgen')
  ) {
    return {
      color: '#ef4444',
      stroke: '#ffffff',
      label: 'Urgente / Spento',
      type: 'rosso',
      isInCarico: false,
    };
  }
  return {
    color: '#f59e0b',
    stroke: '#ffffff',
    label: 'Guasto parziale',
    type: 'giallo',
    isInCarico: false,
  };
}

function createGooglePinIcon(c: Chiamata, isSelected: boolean) {
  const pinState = getChiamataPinState(c);
  const width = isSelected ? 38 : 32;
  const height = isSelected ? 56 : 48;
  const { color, stroke, isInCarico } = pinState;

  const dropShadow = isInCarico
    ? 'filter="drop-shadow(0 0 10px rgba(16, 185, 129, 0.9)) drop-shadow(0 4px 8px rgba(0,0,0,0.4))"'
    : isSelected
    ? 'filter="drop-shadow(0 0 8px rgba(2, 132, 199, 0.8)) drop-shadow(0 4px 6px rgba(0,0,0,0.35))"'
    : 'filter="drop-shadow(0 3px 5px rgba(0,0,0,0.3))"';

  const pulseWaveHtml = isInCarico ? '<div class="pin-pulse-wave"></div>' : '';

  const svg = `
    <div style="position: relative; width: ${width}px; height: ${height}px; display: flex; justify-content: center; align-items: flex-end;">
      ${pulseWaveHtml}
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 42" width="${width}" height="${height}" ${dropShadow}>
        <!-- Ombra alla base -->
        <ellipse cx="14" cy="40" rx="6" ry="2" fill="rgba(0,0,0,0.25)" />
        <!-- Corpo a goccia stile Google Maps con punta in basso -->
        <path d="M14 0 C6.268 0 0 6.268 0 14 C0 24.5 14 40 14 40 S28 24.5 28 14 C28 6.268 21.732 0 14 0 Z"
              fill="${color}" stroke="${stroke}" stroke-width="${isInCarico || isSelected ? 2 : 1.5}" />
        <!-- Foro centrale bianco -->
        <circle cx="14" cy="14" r="5.5" fill="#ffffff" stroke="rgba(0,0,0,0.15)" stroke-width="0.8" />
        <!-- Dot interno corrispondente al colore -->
        <circle cx="14" cy="14" r="${isInCarico ? 3.2 : 2.5}" fill="${color}" />
      </svg>
    </div>
  `;

  return L.divIcon({
    html: svg,
    className: `squadra-map-pin ${isInCarico ? 'is-in-carico' : ''}`,
    iconSize: [width, height],
    iconAnchor: [width / 2, height],
    popupAnchor: [0, -height + 4],
  });
}

export function MappaSquadraModal({
  isOpen,
  onClose,
  chiamate,
  onSelectChiamata,
}: MappaSquadraModalProps) {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const markersRef = useRef<Map<string, L.Marker>>(new Map());
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Filtra chiamate con coordinate valide
  const chiamateValide = useMemo(() => {
    return chiamate.filter((c) => {
      const lat = c.impianto?.lat;
      const lng = c.impianto?.lng;
      return typeof lat === 'number' && typeof lng === 'number' && lat !== 0 && lng !== 0;
    });
  }, [chiamate]);

  // Trova se c'è una chiamata già presa in carico
  const chiamataInCarico = useMemo(() => {
    return chiamate.find((c) => c.inLavorazione);
  }, [chiamate]);

  // Chiamata correntemente selezionata nella mappa per la card inferiore
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    if (chiamataInCarico) {
      setSelectedId(chiamataInCarico.id);
    } else if (chiamateValide.length > 0) {
      setSelectedId((prev) => prev || chiamateValide[0].id);
    }
  }, [chiamataInCarico, chiamateValide]);

  const selectedChiamata = useMemo(() => {
    return chiamate.find((c) => c.id === selectedId) || null;
  }, [chiamate, selectedId]);

  // Mutation per presa in carico diretta dalla mappa
  const accettaRapidaMutation = useMutation({
    mutationFn: async (chiamataId: string) => {
      const { data } = await apiPatch<Chiamata>(`/chiamate/${chiamataId}/accetta`, {});
      return data;
    },
    onSuccess: (updated) => {
      toast({ type: 'success', message: '✅ Chiamata presa in carico (eventuale precedente rilasciata)' });
      queryClient.invalidateQueries({ queryKey: ['pending-calls'] });
      queryClient.invalidateQueries({ queryKey: ['chiamate-aperte'] });
      queryClient.invalidateQueries({ queryKey: ['chiamate-da-terminare'] });
      queryClient.invalidateQueries({ queryKey: ['chiamata', updated.id] });
      setSelectedId(updated.id);
    },
    onError: (err: any) => {
      toast({ type: 'error', message: err?.response?.data?.errore || 'Presa in carico fallita' });
    },
  });

  // Blocca lo scroll del body quando la modale è aperta
  useEffect(() => {
    if (!isOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  const hasFittedInitialBoundsRef = useRef(false);

  // Reset del flag di centratura iniziale alla riapertura del modal
  useEffect(() => {
    if (isOpen) {
      hasFittedInitialBoundsRef.current = false;
    }
  }, [isOpen]);

  const selectedIdRef = useRef(selectedId);
  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);

  // Inizializza la mappa Leaflet e crea i marker
  useEffect(() => {
    if (!isOpen || !mapContainerRef.current) return;

    if (!mapInstanceRef.current) {
      const map = L.map(mapContainerRef.current, {
        zoomControl: false,
        attributionControl: false,
      }).setView(ROMA_CENTER, DEFAULT_ZOOM);

      L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; OpenStreetMap',
      }).addTo(map);

      // Controlli zoom in alto a destra
      L.control.zoom({ position: 'topright' }).addTo(map);

      mapInstanceRef.current = map;
    }

    const map = mapInstanceRef.current;

    // Rimuovi vecchi marker
    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current.clear();

    const bounds = L.latLngBounds([]);

    chiamateValide.forEach((c) => {
      const lat = c.impianto?.lat as number;
      const lng = c.impianto?.lng as number;
      const isSelected = c.id === selectedIdRef.current;

      const marker = L.marker([lat, lng], {
        icon: createGooglePinIcon(c, isSelected),
      }).addTo(map);

      marker.on('click', () => {
        setSelectedId(c.id);
        // Centra la mappa direttamente sul segnaposto selezionato con animazione fluida
        if (mapInstanceRef.current) {
          const currentZoom = mapInstanceRef.current.getZoom();
          const targetZoom = Math.max(currentZoom, 15);
          mapInstanceRef.current.flyTo([lat, lng], targetZoom, {
            animate: true,
            duration: 0.5,
          });
        }
      });

      markersRef.current.set(c.id, marker);
      bounds.extend([lat, lng]);
    });

    // Invalida dimensioni e centra inizialmente la vista
    const timer = setTimeout(() => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.invalidateSize();
        if (!hasFittedInitialBoundsRef.current && bounds.isValid() && chiamateValide.length > 0) {
          if (chiamateValide.length === 1) {
            mapInstanceRef.current.setView(bounds.getCenter(), 15);
          } else {
            mapInstanceRef.current.fitBounds(bounds, { padding: [50, 50], maxZoom: 16 });
          }
          hasFittedInitialBoundsRef.current = true;
        }
      }
    }, 150);

    return () => {
      clearTimeout(timer);
    };
  }, [isOpen, chiamateValide]);

  // Aggiorna l'icona del marker selezionato senza reimpostare i bounds
  useEffect(() => {
    markersRef.current.forEach((marker, id) => {
      const call = chiamateValide.find((item) => item.id === id);
      if (call) {
        marker.setIcon(createGooglePinIcon(call, id === selectedId));
      }
    });
  }, [selectedId, chiamateValide]);

  // Funzione per ricentrare la vista panoramica su tutti gli impianti
  const handleFitAll = () => {
    if (!mapInstanceRef.current || chiamateValide.length === 0) return;
    const bounds = L.latLngBounds([]);
    chiamateValide.forEach((c) => {
      bounds.extend([c.impianto?.lat as number, c.impianto?.lng as number]);
    });
    if (bounds.isValid()) {
      mapInstanceRef.current.fitBounds(bounds, { padding: [50, 50], maxZoom: 16 });
    }
  };

  // Cleanup mappa alla chiusura totale del modal
  useEffect(() => {
    if (!isOpen && mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
      markersRef.current.clear();
    }
  }, [isOpen]);

  // Centra posizione GPS della squadra
  const handleLocateMe = () => {
    if (!navigator.geolocation || !mapInstanceRef.current) {
      toast({ type: 'warning', message: 'Geolocalizzazione non supportata o disabilitata' });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        if (mapInstanceRef.current) {
          mapInstanceRef.current.setView([latitude, longitude], 15);
          toast({ type: 'info', message: '📍 Posizione attuale localizzata' });
        }
      },
      () => {
        toast({ type: 'error', message: 'Impossibile rilevare la posizione GPS' });
      },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  if (!isOpen) return null;
  if (typeof document === 'undefined') return null;

  const currentPinState = selectedChiamata ? getChiamataPinState(selectedChiamata) : null;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-2 sm:p-4 bg-slate-900/80 backdrop-blur-xs animate-fade-in safe-bottom">
      <div className="w-full max-w-4xl h-[94dvh] sm:h-[86vh] rounded-3xl bg-white shadow-2xl flex flex-col overflow-hidden animate-slide-up border border-slate-200 relative">
        {/* Header modale */}
        <div className="p-3.5 sm:p-4 border-b border-slate-100 flex items-center justify-between shrink-0 bg-white z-10">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-9 h-9 rounded-2xl bg-emerald-50 text-emerald-700 flex items-center justify-center font-bold text-lg shrink-0 border border-emerald-200">
              🚦
            </div>
            <div className="min-w-0">
              <h3 className="text-sm sm:text-base font-extrabold text-slate-900 leading-tight truncate">
                Mappa Impianti Assegnati
              </h3>
              <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-0.5 flex-wrap">
                <span>{chiamateValide.length} impianti su mappa</span>
                {chiamataInCarico && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-extrabold text-emerald-800 bg-emerald-100/90 px-1.5 py-0.5 rounded-md border border-emerald-300">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 animate-pulse" />
                    1 in carico
                  </span>
                )}
              </div>
            </div>
          </div>
          <button
            type="button"
            className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-800 flex items-center justify-center transition shrink-0 ml-2"
            onClick={onClose}
            title="Chiudi mappa"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Contenitore Mappa */}
        <div className="flex-1 relative w-full h-full bg-slate-100 overflow-hidden">
          {/* Controlli mappa flottanti in alto a destra, posizionati sotto i pulsanti zoom (+ / -) */}
          <div className="absolute top-[90px] right-[10px] z-[500] flex flex-col gap-2">
            <button
              type="button"
              onClick={handleLocateMe}
              className="w-8 h-8 rounded-lg bg-white border border-slate-300 shadow-md flex items-center justify-center text-slate-700 hover:bg-slate-50 active:scale-95 transition"
              title="Centra la mia posizione"
            >
              <Crosshair className="w-4 h-4 text-emerald-600" />
            </button>
            <button
              type="button"
              onClick={handleFitAll}
              className="w-8 h-8 rounded-lg bg-white border border-slate-300 shadow-md flex items-center justify-center text-slate-700 hover:bg-slate-50 active:scale-95 transition"
              title="Panoramica su tutti gli impianti"
            >
              <Layers className="w-4 h-4 text-sky-600" />
            </button>
          </div>

          {/* Canvas Leaflet */}
          <div ref={mapContainerRef} className="w-full h-full" />

          {/* Avviso se nessun impianto ha coordinate */}
          {chiamateValide.length === 0 && (
            <div className="absolute inset-0 z-[500] flex items-center justify-center p-6 bg-slate-900/40 backdrop-blur-xs">
              <div className="bg-white rounded-2xl p-5 max-w-sm text-center shadow-xl border border-slate-200">
                <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                <h4 className="font-extrabold text-sm text-slate-900">Nessuna coordinata disponibile</h4>
                <p className="text-xs text-slate-600 mt-1">
                  Nessuna delle chiamate assegnate dispone attualmente di coordinate geografiche verificate per essere posizionata sulla mappa.
                </p>
              </div>
            </div>
          )}

          {/* Card Rapida Inferiore Impianto Selezionato */}
          {selectedChiamata && (
            <div className="absolute bottom-2.5 left-2.5 right-2.5 z-[500] bg-white rounded-2xl p-3.5 shadow-2xl border border-slate-200 space-y-2 animate-slide-up">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5">
                  <span className="ln-impianto-chip text-[11px] py-0.5 px-2 font-bold">
                    [{selectedChiamata.impianto?.codice}]
                  </span>
                  <span
                    className={`text-[10px] font-extrabold px-2 py-0.5 rounded-md border ${
                      currentPinState?.type === 'verde'
                        ? 'bg-emerald-50 text-emerald-800 border-emerald-300'
                        : currentPinState?.type === 'rosso'
                        ? 'bg-rose-50 text-rose-800 border-rose-300'
                        : 'bg-amber-50 text-amber-800 border-amber-300'
                    }`}
                  >
                    ● {currentPinState?.label}
                  </span>
                </div>
                {selectedChiamata.inLavorazione ? (
                  <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                    Attiva sul campo
                  </span>
                ) : (
                  <span className="text-[10px] text-slate-400">Assegnata</span>
                )}
              </div>

              <div>
                <h4 className="font-bold text-xs sm:text-sm text-slate-900 leading-tight">
                  {selectedChiamata.impianto?.nome}
                </h4>
                <p className="text-xs text-slate-600 line-clamp-1 mt-0.5">
                  ⚠️ {selectedChiamata.guastoSegnalato || selectedChiamata.descrizione || 'Intervento richiesto'}
                </p>
              </div>

              {/* Azioni rapide: Naviga, Scheda e Prendi in Carico */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-1">
                <button
                  type="button"
                  className="btn-outline justify-center py-2 text-xs font-bold text-sky-800 bg-sky-50/70 hover:bg-sky-100 border-sky-300 gap-1.5"
                  onClick={() => apriGoogleMaps(selectedChiamata.impianto)}
                  title="Apri navigatore"
                >
                  <Navigation className="w-3.5 h-3.5 text-sky-600" />
                  <span>Naviga</span>
                </button>

                {!selectedChiamata.inLavorazione && (
                  <button
                    type="button"
                    className="btn-primary justify-center py-2 text-xs font-bold bg-emerald-600 hover:bg-emerald-700 gap-1.5"
                    disabled={accettaRapidaMutation.isPending}
                    onClick={() => accettaRapidaMutation.mutate(selectedChiamata.id)}
                    title="Prendi in carico questa chiamata (rilascia l'eventuale precedente)"
                  >
                    <PlayCircle className="w-3.5 h-3.5" />
                    <span>{accettaRapidaMutation.isPending ? 'In corso…' : 'Prendi in carico'}</span>
                  </button>
                )}

                <button
                  type="button"
                  className={`btn-primary justify-center py-2 text-xs font-bold gap-1.5 ${
                    selectedChiamata.inLavorazione ? 'col-span-1 bg-emerald-700 hover:bg-emerald-800' : ''
                  }`}
                  onClick={() => {
                    onClose();
                    onSelectChiamata(selectedChiamata.id);
                  }}
                  title="Apri scheda dettagliata chiamata"
                >
                  <span>Scheda</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
